import { useContext, useEffect, useRef } from 'react';
import { PushContext, PushMessage } from './PushContext.tsx';

interface UsePushEventOptions {
  /** Event types to react to, e.g. ['job.progress', 'job.finished']. */
  types: string[];
  /** Called after the connection recovers from a drop — refetch here, lost hints are harmless. */
  onReconnect?: () => void;
}

/**
 * Page/component-scoped reaction to push hints. Scoping = which components are mounted;
 * there are no per-page channels.
 *
 *   usePushEvent(e => { if (e.jobId === jobId) refetch(); }, { types: ['job.progress'], onReconnect: refetch });
 */
export const usePushEvent = (handler: (message: PushMessage) => void, options: UsePushEventOptions) => {
  const { addListener } = useContext(PushContext);

  // Keep latest handler/options without resubscribing every render
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    return addListener(
      message => {
        if (optionsRef.current.types.includes(message.type)) {
          handlerRef.current(message);
        }
      },
      () => optionsRef.current.onReconnect?.(),
    );
  }, []);
};
